/**
 * Data for the report sections of the newer modules (#686): pain point
 * collection and systemic map v2 (ongelmat, ankkurit, juurisyyt, leverage
 * points, the analysis, solution projections) and Ongelmakartta v3's
 * vipupisteet (Vipupisteet v2), Ratkaisut v4's solutions and Tulevaisuustestaus v2. Fetches only what the enabled
 * sections need. The v2 endpoints still require a sign-in while they are in
 * development, so credentials are sent; a failed fetch just means no data.
 */
import { useEffect, useState } from 'react';
import API_ENDPOINTS from '../../config/api';
import { futureV2Sections } from './ReportFutureV2';

export const SYSTEMIC_V2_SECTIONS = ['ongelmat', 'ankkurit', 'juurisyyt', 'leverage-points', 'systemic-analysis', 'solution-projections'];
export const CHAIN_SECTIONS = ['pain-points', ...SYSTEMIC_V2_SECTIONS, 'vipupisteet-v2', 'solutions-v4', 'future-interplay-v2', 'future-timeline-v2'];
/** Section id → report.nav.* key */
export const CHAIN_NAV_KEYS = {
  'pain-points': 'painPoints',
  ongelmat: 'ongelmat',
  ankkurit: 'ankkurit',
  juurisyyt: 'juurisyyt',
  'leverage-points': 'leveragePoints',
  'vipupisteet-v2': 'vipupisteetV2',
  'systemic-analysis': 'systemicAnalysis',
  'solution-projections': 'solutionProjections',
  'solutions-v4': 'solutionsV4',
  'future-interplay-v2': 'futureInterplayV2',
  'future-timeline-v2': 'futureTimelineV2',
};

async function get(url) {
  try {
    const res = await fetch(url, { credentials: 'include' });
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
}

/** Which of the chain sections have something to show. */
export function sectionsWithData(data) {
  const has = new Set();
  if (data.painPoints?.clusters?.length) has.add('pain-points');
  if (data.map?.ongelmat?.length) has.add('ongelmat');
  if (data.map?.anchors?.length) has.add('ankkurit');
  if (data.map?.juurisyyt?.length) has.add('juurisyyt');
  if (data.map?.vipupisteet?.length) has.add('leverage-points');
  if (data.analysis?.content?.overview) has.add('systemic-analysis');
  if (data.pivotsV3?.vipupisteet?.length) has.add('vipupisteet-v2');
  if (data.portfolio?.metrics?.some((m) => m.solutions?.length)) has.add('solution-projections');
  if (data.solutionsV4?.solutions?.some((s) => s.assessment)) has.add('solutions-v4');
  futureV2Sections(data.futureV2).forEach((id) => has.add(id));
  return has;
}

/**
 * @param {string} instanceId
 * @param {(id: string) => boolean} enabled whether the chain (and access) includes a section
 */
export default function useChainReportData(instanceId, enabled) {
  const [data, setData] = useState({});
  const wantPain = enabled('pain-points');
  const wantV2 = SYSTEMIC_V2_SECTIONS.some((id) => enabled(id));
  const wantAnalysis = enabled('systemic-analysis');
  const wantPortfolio = enabled('solution-projections');
  const wantPivotsV3 = enabled('vipupisteet-v2');
  const wantSolutionsV4 = enabled('solutions-v4');
  const wantFutureV2 = enabled('future-interplay-v2') || enabled('future-timeline-v2');

  useEffect(() => {
    if (!instanceId || (!wantPain && !wantV2 && !wantPivotsV3 && !wantSolutionsV4 && !wantFutureV2)) return undefined;
    let cancelled = false;
    const kid = `kid=${instanceId}`;
    Promise.all([
      wantPain ? get(API_ENDPOINTS.SYSTEMIC_MAP_PAIN_POINTS(kid)) : null,
      wantV2 ? get(API_ENDPOINTS.SYSTEMIC_V2_MAP(kid)) : null,
      wantAnalysis ? get(API_ENDPOINTS.SYSTEMIC_V2_ANALYSIS(kid)) : null,
      wantPortfolio ? get(API_ENDPOINTS.SYSTEMIC_V2_PORTFOLIO(kid)) : null,
      wantPivotsV3 ? get(API_ENDPOINTS.SYSTEMIC_MAP_V3_PIVOTS(instanceId)) : null,
      wantSolutionsV4 ? get(API_ENDPOINTS.SOLUTIONS_V4_BOARD(instanceId)) : null,
      wantFutureV2 ? get(API_ENDPOINTS.FUTURE_V2_STATE(instanceId)) : null,
    ]).then(([painPoints, map, analysis, portfolio, pivotsV3, boardV4, futureV2]) => {
      if (cancelled) return;
      // Participant names and the facilitator prompt are not report content
      // A report viewer gets size + inputCount; a session for the instance the whole collection (#1234)
      const clusters = (painPoints?.clusters || []).map((c) => ({
        id: c.id, title: c.title, status: c.status, count: c.size ?? c.memberInputIds?.length ?? 0,
      }));
      setData({
        painPoints: painPoints ? { clusters, inputCount: painPoints.inputCount ?? painPoints.inputs?.length ?? 0 } : null,
        map,
        analysis: analysis?.analysis || analysis,
        portfolio: portfolio?.portfolio || portfolio,
        pivotsV3,
        // Ratkaisut v4's board without which device wrote what: not report content
        solutionsV4: boardV4 ? {
          pivots: boardV4.pivots?.vipupisteet || [],
          solutions: (boardV4.solutions || []).map(({ participantId: _device, ...s }) => s),
          kooste: boardV4.kooste || null,
        } : null,
        // Tulevaisuustestaus v2 without this device's votes: not report content
        futureV2: futureV2 ? { ...futureV2, voting: null } : null,
      });
    });
    return () => { cancelled = true; };
  }, [instanceId, wantPain, wantV2, wantAnalysis, wantPortfolio, wantPivotsV3, wantSolutionsV4, wantFutureV2]);

  return { data, available: sectionsWithData(data) };
}
